/* Tenant management — login, switch, dropdown */

import { state, refs, esc, lockInput, unlockInput } from './state.js';
import { api } from './api.js';
import { getAuthMode, useConnection, getActiveConnection, getActiveConversation, openAddConnection } from './auth.js';
import { dotStateClass } from './conn-dots.js';
import { reconnectWs } from './ws.js';
import { confirm } from './dialogs.js';
import { openRemoveMenu, confirmRemoveConnection } from './connection-delete.js';
import { addAssistantMessage } from './chat.js';
import { notify } from './toast.js';
import { loadFileTree, loadSyncStatuses } from './file-tree.js';
import {
  saveCurrentWorkspace,
  tearDownTenantScopedUI,
  restoreWorkspace,
  collectLostItems,
  confirmLossDialog,
} from './workspace.js';


// ---------------------------------------------------------------------------
// Tenant data
// ---------------------------------------------------------------------------

export function tenantHostFromUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    return new URL(raw).host;
  } catch {
    return raw
      .replace(/^https?:\/\//, '')
      .replace(/\/Login\.aspx\/?$/i, '')
      .replace(/\/$/, '');
  }
}

export function tenantDisplayLabel(tenant) {
  if (!tenant) return '';
  if (typeof tenant === 'string') {
    const found = (state.tenants || []).find(t => t.name === tenant);
    return tenantDisplayLabel(found) || tenant;
  }
  return tenantHostFromUrl(tenant.url) || tenant.name || '';
}

function _tenantByName(name) {
  return (state.tenants || []).find(t => t.name === name) || null;
}

function _tenantTitle(tenant) {
  if (!tenant) return '';
  return tenant.url || tenant.name || '';
}

function _setActiveTenantDisplay(name) {
  const tenant = _tenantByName(name);
  const label = tenantDisplayLabel(tenant) || name || '';
  if (refs.tenantName) {
    refs.tenantName.textContent = label;
    refs.tenantName.title = _tenantTitle(tenant);
  }
  if (refs.tenantSelector) refs.tenantSelector.value = name || '';
}

// v2 shell (M9 T2): populates the sidebar conn-card's #v2TenantEnv/#v2TenantHost
// spans -- called ADDITIVELY alongside every existing #v2TenantLabel update
// (main.js's initV2Shared() post-loadTenants(), and this file's own
// switchTenant()/submitLogin() post-switch/post-login hooks below). Reuses
// tenantHostFromUrl() (the SAME host-only string _updateStatusBarTenantUrl()
// above and tenantDisplayLabel() itself already compute) for the host, and the
// tenant record's own `env` field (the SAME data _renderTenantRow()'s dropdown
// env badge reads) for the lineage chip. Both via safe DOM-property
// assignment (.textContent/.hidden), same as every other call site in this
// file that updates these elements -- no HTML built here, so no escaping is
// needed. #v2TenantEnv is hidden (not shown as a blank pill) whenever the
// resolved tenant has no `env` set, or no tenant resolves at all -- mirrors
// #v2SyncBadges's own "hidden, not a placeholder, when unknown" convention
// from M9 T1. No-op if neither span exists (v1, or an unbooted v2 shell).
export function updateTenantCardMeta(name) {
  const tenant = _tenantByName(name);

  const envEl = document.getElementById("v2TenantEnv");
  const hostEl = document.getElementById("v2TenantHost");
  if (envEl || hostEl) {
    const env = tenant?.env || "";
    if (envEl) {
      envEl.textContent = env;
      envEl.hidden = !env;
    }
    if (hostEl) hostEl.textContent = tenant ? tenantHostFromUrl(tenant.url) : "";
  }

  // M10 T3: sidebar account card identity -- LOCAL MODE ONLY. Hosted mode's
  // identity comes from auth.js's cached /api/me email, rendered once at
  // boot by shell-v2.js's _sidebarFootHtml() -- hosted has no "tenants"
  // concept, so this function's other call sites (initV2Shared()
  // post-loadTenants(), which 401s and resolves name=undefined in hosted
  // mode; switchTenant()'s success/rollback paths) must NOT clobber the real
  // hosted identity back to the honest-but-wrong "Not connected" fallback
  // below -- this guard is load-bearing, not defensive-for-its-own-sake.
  if (getAuthMode() === "hosted") return;
  const nameEl = document.getElementById("acctName");
  const avatarEl = document.getElementById("acctAvatar");
  if (!nameEl && !avatarEl) return;
  const identity = tenant?.username || "";
  if (nameEl) nameEl.textContent = identity || "Not connected";
  if (avatarEl) avatarEl.textContent = identity ? identity[0].toUpperCase() : "?";
}

// Hosted counterpart of updateTenantCardMeta(): fill the v2 sidebar conn-card's
// label/env-badge/host from an active-connection record ({label, client_label, env_role,
// sap_base_url}, as api.connectionMeta() returns). Called at hosted boot (main.js's
// initV2Shared()) and after each in-place switch (switchConnection() below). Reuses
// tenantHostFromUrl() -- the SAME host-only parse updateTenantCardMeta() uses for
// #v2TenantHost -- rather than a second host helper. Safe DOM-property assignment only
// (no HTML built), so no escaping is needed; no-op for any element the shell omits.
export function updateHostedConnCardMeta(meta) {
  if (!meta) return;
  const host = tenantHostFromUrl(meta.sap_base_url);
  const labelEl = document.getElementById("v2TenantLabel");
  const envEl = document.getElementById("v2TenantEnv");
  const hostEl = document.getElementById("v2TenantHost");
  if (labelEl) labelEl.textContent = meta.label || meta.client_label || host || "";
  if (envEl) {
    envEl.textContent = meta.env_role || "";
    envEl.hidden = !meta.env_role;
  }
  if (hostEl) hostEl.textContent = host;
}

export async function loadTenants() {
  let didSwitch = false;
  try {
    const result = await api.listTenants();
    state.tenants = result.tenants;
    _populateTenantSelector();
    const active = state.tenants.find(t => t.active);
    if (active) {
      state.tenant = active.name;
      _setActiveTenantDisplay(active.name);
      _updateStatusBarTenantUrl();
    } else if (state.tenants.length > 0) {
      const saved = state.tenants.find(t => t.saved);
      if (saved) {
        await switchTenant(saved.name);
        didSwitch = true;
      }
    }
  } catch (e) {
    console.error("Failed to load tenants:", e);
  }
  return didSwitch;
}

/**
 * Populate the top-nav <select> with tenant options.
 */
function _populateTenantSelector() {
  const sel = refs.tenantSelector;
  if (!sel) return;
  sel.innerHTML = '';
  for (const t of state.tenants) {
    const opt = document.createElement('option');
    opt.value = t.name;
    opt.textContent = tenantDisplayLabel(t);
    opt.title = _tenantTitle(t);
    if (t.active) opt.selected = true;
    sel.appendChild(opt);
  }
  // Add "Add tenant..." option
  const addOpt = document.createElement('option');
  addOpt.value = '__add__';
  addOpt.textContent = '+ Add tenant...';
  sel.appendChild(addOpt);
}

// ---------------------------------------------------------------------------
// Dropdown — grouped by family
// ---------------------------------------------------------------------------

const _ENV_RANK = { dev: 0, test: 1, prod: 2 };

function _groupTenantsByFamily(tenants) {
  // Returns [{family, rows:[tenant...]}], families alpha-sorted, then an
  // "Ungrouped" group (family === null) last. Rows ordered by env rank.
  const byFam = new Map();
  for (const t of tenants) {
    const key = t.family || null;
    if (!byFam.has(key)) byFam.set(key, []);
    byFam.get(key).push(t);
  }
  const groups = [];
  for (const fam of [...byFam.keys()].filter(Boolean).sort()) {
    const rows = byFam.get(fam).slice().sort(
      (a, b) => (_ENV_RANK[a.env] ?? 9) - (_ENV_RANK[b.env] ?? 9));
    groups.push({ family: fam, rows });
  }
  if (byFam.has(null)) groups.push({ family: null, rows: byFam.get(null) });
  return groups;
}

function _renderTenantRow(t) {
  const label = tenantDisplayLabel(t);
  const item = document.createElement("div");
  item.className = `td-item${t.active ? " active" : ""}`;
  const envBadge = t.env ? `<span class="td-env td-env-${esc(t.env)}">${esc(t.env)}</span>` : "";
  item.innerHTML = `
    <div class="td-item-row">
      <div class="td-item-main">
        <span class="td-dot ${t.connected ? "connected" : ""}"></span>
        <div class="td-item-info">
          <span class="td-name" title="${esc(_tenantTitle(t))}">${esc(label)}</span>
          ${t.username ? `<span class="td-user">${esc(t.username)}</span>` : ""}
        </div>
      </div>
      ${envBadge}
      ${t.saved ? `<button class="td-lineage-btn" title="Edit family / environment"><span class="ic ic-edit" aria-hidden="true"></span></button>` : ""}
      ${!t.saved ? `<span class="td-unsaved" title="Not saved">*</span>` : ""}
      ${t.saved && !t.active ? `<button class="td-remove" title="Remove">&times;</button>` : ""}
    </div>
    <div class="td-lineage-editor" hidden>
      <input class="td-fam-input" type="text" placeholder="family" />
      <select class="td-env-select">
        <option value=""${!t.env ? " selected" : ""}>—</option>
        <option value="dev"${t.env === "dev" ? " selected" : ""}>Dev</option>
        <option value="test"${t.env === "test" ? " selected" : ""}>Test</option>
        <option value="prod"${t.env === "prod" ? " selected" : ""}>Prod</option>
      </select>
      <button class="td-lineage-save">Save</button>
    </div>
  `;
  // Set family via DOM property (not HTML attribute) — injection-proof: property
  // assignment is not HTML-parsed, so any character including quotes is safe data.
  item.querySelector(".td-fam-input").value = t.family || "";
  // Switch / remove (unchanged behavior)
  item.querySelector(".td-item-main").addEventListener("click", async () => {
    if (t.active) { closeTenantDropdown(); return; }
    if (t.saved) { await switchTenant(t.name); } else { openLoginDialog(t.name); }
    closeTenantDropdown();
  });
  const removeBtn = item.querySelector(".td-remove");
  if (removeBtn) {
    removeBtn.addEventListener("click", async (e) => {
      e.stopPropagation();
      const ok = await confirm("Remove Tenant", `Remove saved credentials for "${label}"?`);
      if (ok) { await api.tenantRemove(t.name); await loadTenants(); renderTenantDropdown(); }
    });
  }
  // Lineage editor toggle + save
  const editBtn = item.querySelector(".td-lineage-btn");
  const editor = item.querySelector(".td-lineage-editor");
  if (editBtn && editor) {
    editBtn.addEventListener("click", (e) => { e.stopPropagation(); editor.hidden = !editor.hidden; });
    item.querySelector(".td-lineage-save").addEventListener("click", async (e) => {
      e.stopPropagation();
      const family = item.querySelector(".td-fam-input").value.trim();
      const env = item.querySelector(".td-env-select").value;
      await api.setLineage(t.name, family, env);
      await loadTenants();
      renderTenantDropdown();
    });
  }
  return item;
}

// Hosted dropdown body: one row per user connection (api.connections()), reusing the SAME
// .td-item/.td-dot/.td-name/.td-env markup + CSS the local _renderTenantRow() uses. The active
// connection (getActiveConnection()) is marked (active class + lit dot) and its row is inert;
// any other row switches in place (switchConnection()). Fire-and-forget async: the list fills
// when api.connections() resolves. On fetch failure the list is left empty (the card still shows
// the active connection); "+ Add a connection" stays reachable from the divider button below.
async function _renderHostedConnList() {
  const list = refs.tenantList;
  if (!list) return;
  list.innerHTML = "";
  let conns = [];
  try {
    const d = await api.connections();
    conns = (d && d.connections) || [];
  } catch {
    return;
  }
  const activeId = getActiveConnection();
  const probes = [];
  for (const c of conns) {
    const isActive = c.id === activeId;
    const label = c.label || tenantHostFromUrl(c.sap_base_url) || "Connection";
    const sub = c.client_label || tenantHostFromUrl(c.sap_base_url) || "";
    const envBadge = c.env_role ? `<span class="td-env td-env-${esc(c.env_role)}">${esc(c.env_role)}</span>` : "";
    const item = document.createElement("div");
    item.className = `td-item${isActive ? " active" : ""}`;
    item.dataset.cid = c.id; // DOM property (not HTML) -- injection-proof row identifier
    item.innerHTML = `
      <div class="td-item-row">
        <div class="td-item-main">
          <span class="td-dot ${isActive ? dotStateClass(state.connHealth) : "connecting"}"></span>
          <div class="td-item-info">
            <span class="td-name" title="${esc(sub || label)}">${esc(label)}</span>
            ${sub ? `<span class="td-user">${esc(sub)}</span>` : ""}
          </div>
        </div>
        ${envBadge}
        <button class="td-kebab" type="button" aria-label="Connection actions" aria-haspopup="menu" title="More actions"><span class="ic ic-sm ic-more-vertical" aria-hidden="true"></span></button>
      </div>`;
    const main = item.querySelector(".td-item-main");
    if (isActive) {
      main.addEventListener("click", () => closeTenantDropdown());
    } else {
      main.addEventListener("click", () => { closeTenantDropdown(); switchConnection(c.id); });
    }
    // Kebab → one-item "Remove connection" menu (delete a deliberate level in, never a stray row
    // click). Present on every row incl. the active one — see _removeHostedConnection's switch-out
    // routing. stopPropagation so the opening click neither switches the row nor hits the menu's
    // own document close-listener.
    const kebab = item.querySelector(".td-kebab");
    kebab.addEventListener("click", (e) => {
      e.stopPropagation();
      openRemoveMenu(kebab, () => _removeHostedConnection(c, label, isActive));
    });
    list.appendChild(item);
    probes.push({ c, dot: item.querySelector(".td-dot") });
  }
  // Live health per row: every listed connection is probed concurrently through
  // the server-cached health endpoint, so dots resolve independently. The ACTIVE
  // row's result also feeds state.connHealth -- the same signal the header dot
  // renders from -- so the two can never disagree.
  for (const { c, dot } of probes) {
    api.connectionHealth(c.id).then(
      (h) => _applyRowHealth(list, dot, c.id, !!(h && h.ok)),
      () => _applyRowHealth(list, dot, c.id, false),
    );
  }
}

// Resolve one dropdown row's probe: recolor its dot unless the dropdown was
// re-rendered/closed meanwhile (the stale dot is no longer in the list), and
// keep the header signal in sync for the active connection. A probe error must
// land on "off", never leave the amber in-flight state stuck.
function _applyRowHealth(list, dot, cid, ok) {
  if (getActiveConnection() === cid) state.connHealth = ok ? "ok" : "off";
  if (!dot || !list.contains(dot)) return;
  dot.classList.remove("connected", "connecting", "off");
  dot.classList.add(ok ? "connected" : "off");
}

// Kebab → "Remove connection": show the shared hardened confirm (naming label/host/env; the active
// connection's copy also warns you'll be switched out; prod gets a typed-confirm), DELETE via
// api.deleteConnection, then route + feedback. A non-active delete re-renders the dropdown list in
// place; the ACTIVE connection routes out via _afterActiveConnectionDeleted. Success → a brief
// toast; failure → an error toast with the row left intact. Labels reach confirm/toast via
// textContent, so no escaping is needed here.
const _deletingConns = new Set(); // in-flight ids — a second trigger can't fire a duplicate DELETE
async function _removeHostedConnection(c, label, isActive) {
  const res = await confirmRemoveConnection({
    label, host: tenantHostFromUrl(c.sap_base_url), envRole: c.env_role, isActive,
  });
  if (!res || _deletingConns.has(c.id)) return;
  _deletingConns.add(c.id);
  let deleted = false;
  try {
    await api.deleteConnection(c.id, res.typedConfirm); // prod sends typed_confirm; non-prod sends null
    deleted = true;
  } catch (e) {
    notify(e.message || "Could not remove the connection", "error"); // row stays (no re-render)
  } finally {
    _deletingConns.delete(c.id);
  }
  if (!deleted) return;
  notify("Connection removed", "success");
  if (isActive) {
    await _afterActiveConnectionDeleted(c.id);
  } else {
    renderTenantDropdown(); // re-fetch + re-render the list in place (dropdown stays open)
  }
}

// After deleting the connection the app is currently bound to: switch to another remaining
// connection in place (switchConnection — no reload), or, when none remain, open the add-connection
// overlay in-app (openAddConnection(switchConnection) — creating one switches straight into it). A
// smooth in-app transition, no page reload; every tenant-scoped surface rebinds via switchConnection.
async function _afterActiveConnectionDeleted(deletedId) {
  closeTenantDropdown();
  let conns = [];
  try {
    const d = await api.connections();
    conns = (d && d.connections) || [];
  } catch {
    /* treat as none remaining */
  }
  const next = conns.find((x) => x.id !== deletedId);
  if (next) {
    await switchConnection(next.id);
  } else {
    openAddConnection(switchConnection); // no connections left → the add-connection empty state
  }
}

export function renderTenantDropdown() {
  const list = refs.tenantList;
  if (!list) return;
  // Hosted: connections (the tenant registry is a gated legacy surface); local: saved tenants.
  if (getAuthMode() === "hosted") { _renderHostedConnList(); return; }
  list.innerHTML = "";
  for (const group of _groupTenantsByFamily(state.tenants || [])) {
    const header = document.createElement("div");
    header.className = "td-group-header";
    header.textContent = group.family || "Ungrouped";
    list.appendChild(header);
    for (const t of group.rows) {
      list.appendChild(_renderTenantRow(t));
    }
  }
}

function _updateStatusBarTenantUrl() {
  if (!refs.sbBranch) return;
  const active = (state.tenants || []).find(t => t.name === state.tenant);
  const url = active?.url || '';
  refs.sbBranch.textContent = url
    .replace(/^https?:\/\//, '')
    .replace(/\/Login\.aspx\/?$/i, '')
    .replace(/\/$/, '');
  refs.sbBranch.title = url ? `SAP CPQ tenant URL — ${url}` : 'SAP CPQ tenant URL';
}

export async function switchTenant(name) {
  if (state.tenantSwitching) return; // switch already in progress
  const from = state.tenant;
  const targetLabel = tenantDisplayLabel(name);
  lockInput();
  state.tenantSwitching = true;
  // Sidebar conn-card dot (shell-v2.js's updateConnDot()): CONNECTING from the
  // very start of the attempt. Resolved back to false on every exit path below
  // -- the declined-loss early return, the ordinary success path, and the
  // catch block -- except the "stays locked for initial sync" branch, which
  // defers to the SAME WS `initial_sync_done` handler (main.js) that already
  // defers unlockInput() for that case.
  state.tenantConnecting = true;

  // 1. Save the current tenant's workspace before tearing down.
  //    On failure, ask the user whether to proceed and lose state.
  if (from) {
    try {
      await saveCurrentWorkspace(from);
    } catch (e) {
      const inventory = collectLostItems();
      const proceed = await confirmLossDialog(from, inventory, e);
      if (!proceed) {
        unlockInput();
        state.tenantSwitching = false;
        state.tenantConnecting = false;
        _setActiveTenantDisplay(from);
        return;
      }
    }
  }

  // 2. Tear down tenant-scoped UI (close tabs, clear chat).
  tearDownTenantScopedUI();

  try {
    _setActiveTenantDisplay(name);
    const result = await api.tenantSwitch(name);
    state.tenant = name;
    state.openFolders.clear();
    // Drop the diff cache + expanded-diff state — they belong to the previous
    // tenant; a same-named file (e.g. foo.py) in the new tenant must NOT show
    // the old tenant's cached diff.
    state.diffCache.clear();
    state.expandedDiffNames.clear();
    // Clear pipeline/execution-order caches — they belong to the previous tenant
    state.fieldsTree = null;
    state.fieldsTreeSource = null;
    state.pipelineData = null;
    state.fieldCalcsLoading = {};
    // Update active state locally — avoids redundant GET /api/tenants
    for (const t of state.tenants) {
      t.active = (t.name === name);
      if (t.name === name) t.connected = true;
    }

    _updateStatusBarTenantUrl();

    // v2 shell: stale lazy views (Code/Exec/Promotion/Timeline mounts hold the OLD
    // tenant's data) + topbar label. No-op in v1 (element absent). Placed before the
    // is_new_tenant branch below so it covers both outcomes of a SUCCESSFUL switch.
    // Dynamic import avoids a cycle: shell-v2.js already imports tenantDisplayLabel
    // from this file, so a static import here (tenants.js -> shell-v2.js) would close
    // the loop back to tenants.js -> shell-v2.js -> tenants.js.
    const v2label = document.getElementById("v2TenantLabel");
    if (v2label) {
      const { resetViewGuards } = await import("./shell-v2.js");
      resetViewGuards();
      v2label.textContent = tenantDisplayLabel(name);
      updateTenantCardMeta(name); // M9 T2: sidebar card's env/host, additive
    }

    if (result.is_new_tenant) {
      // stays locked — initial_sync_done will unlock
      // (state.tenantConnecting and state.tenantSwitching stay true too —
      // the same WS handler resolves both)
    } else {
      await loadFileTree();
      loadSyncStatuses(false);

      // 3. Load and restore the new tenant's saved workspace.
      try {
        const ws = await api.workspaceGet(name);
        await restoreWorkspace(ws);
      } catch (e) {
        // Workspace load failure should not block the switch — log + continue.
        console.error('workspace restore failed', e);
      }

      unlockInput();
      state.tenantSwitching = false;
      state.tenantConnecting = false;
    }
  } catch (e) {
    // Auto-rollback to previous tenant so app state stays consistent
    // regardless of whether the user notices the error toast.
    const previous = from || state.tenant;
    state.tenant = previous || null;
    _setActiveTenantDisplay(previous);
    for (const t of state.tenants) {
      t.active = (t.name === previous);
    }
    _updateStatusBarTenantUrl();

    // v2 shell: mirror the success-path hook above (same gated/no-op-in-v1
    // pattern) so the topbar doesn't keep showing the FAILED-to tenant while
    // state.tenant has correctly rolled back to `previous`.
    const v2label = document.getElementById("v2TenantLabel");
    if (v2label) {
      const { resetViewGuards } = await import("./shell-v2.js");
      resetViewGuards();
      v2label.textContent = tenantDisplayLabel(previous);
      updateTenantCardMeta(previous); // M9 T2: sidebar card's env/host, additive
    }

    const previousLabel = previous ? tenantDisplayLabel(previous) : "no tenant";
    addAssistantMessage(
      `Failed to switch to **${targetLabel}**: ${e.message}. Reverted to **${previousLabel}**.`
    );
    unlockInput();
    state.tenantConnecting = false;
    state.tenantSwitching = false;
    // The overlay's own inline error card + Retry button is gone — surface the same
    // message via the app's existing error-toast idiom instead. No retry affordance:
    // the user can re-pick the tenant from the switcher.
    notify(e.message || "Unknown error", "error");
  }
}

// Hosted in-place connection switch (the counterpart of switchTenant() for hosted mode): rebind
// the whole UI session to another of the user's connections WITHOUT a page reload / the boot
// picker. Reuses the existing primitives -- useConnection() (auth.js: sets the connection id,
// resumes/mints its conversation, setActiveSession), reconnectWs() (rebinds the run socket to the
// new cid+coid), window.renderStage/loadConversationMessages (chat replay), resetViewGuards()
// (shell-v2, clears the lazy v2 views) + resetDatabaseStats() (clears the tenant-keyed stats memo),
// api.connectionMeta()/connectionHealth() (card + dot) -- nothing here is reimplemented. The
// connSwitching guard and the getActiveConversation()/getActiveConnection() re-checks drop any
// late response from a superseded switch (a fast double-switch can't let an old connection win).
export async function switchConnection(cid) {
  if (state.connSwitching || cid === getActiveConnection()) return;
  state.connSwitching = true;
  state.connHealth = "connecting";
  try {
    await useConnection(cid);
    const coid = getActiveConversation();
    reconnectWs();
    window.renderStage?.(coid);
    const d = await api.connectionMessages(cid, coid).catch(() => null);
    // Stale-switch guard: only replay if this conversation is still the active one.
    if (getActiveConversation() === coid && d?.messages?.length) window.loadConversationMessages(d.messages);
    const { resetViewGuards } = await import("./shell-v2.js");
    resetViewGuards();
    const dbMod = await import("./database-view.js");
    dbMod.resetDatabaseStats?.();
    const meta = await api.connectionMeta().catch(() => null);
    if (meta) updateHostedConnCardMeta(meta);
    // The @-autocomplete's tree is per-connection: refresh it for the new
    // tenant (fire-and-forget, same soft-fail contract as the boot load).
    // Guard against stale-switch races: a rapid A→B→C switch could have B's
    // slow tree response land after C's, clobbering state with the wrong tree;
    // self-heal by re-loading if the active connection has drifted.
    const treeCid = cid;
    loadFileTree().then(() => {
      if (getActiveConnection() !== treeCid) loadFileTree().catch(() => {});
    }).catch(() => {});
    // Refresh the run/session rail (best-effort; skip silently if the module isn't present).
    import("./session-rail.js").then((m) => m.initRail?.()).catch(() => {});
    // Health probe is FIRE-AND-FORGET: the switch is functionally complete here (card,
    // label, views all rebound), but the SAP probe can run up to the server's multi-second
    // budget against a dead connection. AWAITING it would hold state.connSwitching (the
    // re-entrancy guard, cleared in finally) for that whole window, blocking the user from
    // re-switching. So kick it detached and let finally clear connSwitching immediately.
    // connHealth is already "connecting" (set at the top), so the dot spins until this
    // resolves; the getActiveConnection()===cid guard drops a late probe from a
    // switched-away connection so it can't clobber the current connection's dot.
    api.connectionHealth(cid).then(
      (h) => { if (getActiveConnection() === cid) state.connHealth = h.ok ? "ok" : "off"; },
      () => { if (getActiveConnection() === cid) state.connHealth = "off"; },
    );
  } catch (e) {
    state.connHealth = "off";
    notify(e.message || "Could not switch connection", "error");
  } finally {
    state.connSwitching = false;
  }
}

export function toggleTenantDropdown() {
  state.tenantDropdownOpen = !state.tenantDropdownOpen;
  if (refs.tenantDropdown) refs.tenantDropdown.hidden = !state.tenantDropdownOpen;
  if (state.tenantDropdownOpen) renderTenantDropdown();
}

export function closeTenantDropdown() {
  state.tenantDropdownOpen = false;
  if (refs.tenantDropdown) refs.tenantDropdown.hidden = true;
}

// ---------------------------------------------------------------------------
// Login dialog
// ---------------------------------------------------------------------------

// Auth-method segmented toggle (Password | Client credentials) -- mirrors the hosted
// add-connection form's UX (auth.js:_renderAddConnection/_submitConnection): a two-button
// group toggles which credential field group is visible, both stay in the DOM (typed
// values survive switching back and forth). Exported so main.js can wire the buttons'
// click handlers once at init (the dialog markup is static, not re-rendered per-open --
// unlike auth.js's overlay -- so listeners must NOT be (re)attached inside openLoginDialog).
export function setLoginAuthMode(mode) {
  const cc = mode === "client_credentials";
  refs.loginAuthMode.querySelectorAll("button").forEach((b) => {
    const on = b.dataset.mode === mode;
    b.classList.toggle("is-active", on);
    b.setAttribute("aria-pressed", String(on));
  });
  refs.loginPassFields.hidden = cc;
  refs.loginCcFields.hidden = !cc;
}

export function openLoginDialog(prefilledName) {
  refs.loginUrl.value = prefilledName
    ? `https://${prefilledName}.cpq.cloud.sap/Login.aspx`
    : "";
  refs.loginUsername.value = "";
  refs.loginPassword.value = "";
  refs.loginClientId.value = "";
  refs.loginClientSecret.value = "";
  refs.loginRemember.checked = true;
  refs.loginError.hidden = true;
  setLoginAuthMode("password");
  refs.loginDialog.showModal();
  refs.loginUrl.focus();
}

export async function submitLogin() {
  const url = refs.loginUrl.value.trim();
  const authMode = refs.loginAuthMode.querySelector(".is-active")?.dataset.mode || "password";
  const remember = refs.loginRemember.checked;

  // Only the active mode's credential pair is sent (mirrors the hosted form): the other
  // pair's fields stay their defaults ("" / undefined) below.
  let username = "", password = "", clientId = "", clientSecret = "";
  if (authMode === "client_credentials") {
    clientId = refs.loginClientId.value.trim();
    clientSecret = refs.loginClientSecret.value; // not trimmed -- a secret's exact bytes matter
    if (!url || !clientId || !clientSecret) {
      refs.loginError.textContent = "CPQ URL, client ID, and client secret are required.";
      refs.loginError.hidden = false;
      return;
    }
  } else {
    username = refs.loginUsername.value.trim();
    password = refs.loginPassword.value.trim();
    if (!url || !username || !password) {
      refs.loginError.textContent = "All fields are required.";
      refs.loginError.hidden = false;
      return;
    }
  }

  refs.loginSubmit.disabled = true;
  refs.loginSubmit.textContent = "Connecting...";
  refs.loginError.hidden = true;
  // Sidebar conn-card dot: CONNECTING from here -- the true start of the
  // attempt (earlier than state.tenantSwitching below, which is only set once
  // login has already succeeded and a real tenant name/label exists). Resolved
  // in the catch block below and in the ordinary success path; the "stays
  // locked for initial sync" branch defers to the initial_sync_done WS
  // handler, same as switchTenant()'s identical branch above.
  state.tenantConnecting = true;

  try {
    const result = await api.tenantLogin({
      url, username, password, remember,
      authMode, clientId, clientSecret,
    });
    refs.loginDialog.close();
    lockInput();
    state.tenantSwitching = true;
    state.tenant = result.name;
    state.openFolders.clear();
    await loadTenants();
    _setActiveTenantDisplay(result.name);

    // v2 shell: mirror switchTenant()'s own hook (see its comment above) --
    // stale lazy views + topbar label, placed before the is_new_tenant branch
    // so it covers both outcomes of a successful login/switch. No-op in v1
    // (element absent).
    const v2label = document.getElementById("v2TenantLabel");
    if (v2label) {
      const { resetViewGuards } = await import("./shell-v2.js");
      resetViewGuards();
      v2label.textContent = tenantDisplayLabel(result.name);
      updateTenantCardMeta(result.name); // M9 T2: sidebar card's env/host, additive
    }

    if (result.is_new_tenant) {
      // stays locked — initial_sync_done will unlock
      // (state.tenantConnecting and state.tenantSwitching stay true too —
      // the same WS handler resolves both)
    } else {
      await loadFileTree();
      loadSyncStatuses();
      unlockInput();
      state.tenantSwitching = false;
      state.tenantConnecting = false;
    }
    _updateStatusBarTenantUrl();
  } catch (e) {
    refs.loginError.textContent = e.message || "Login failed";
    refs.loginError.hidden = false;
    state.tenantConnecting = false;
    // A failure after the post-login lock/guard were set must release both,
    // or the app stays input-locked and switchTenant()'s re-entrancy guard
    // blocks every later switch until a reload. (The intentional stay-locked
    // case -- is_new_tenant deferring to initial_sync_done -- is a success
    // path and never reaches this catch.)
    state.tenantSwitching = false;
    unlockInput();
  } finally {
    refs.loginSubmit.disabled = false;
    refs.loginSubmit.textContent = "Connect";
  }
}
